import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Message, useMessage } from '@messageformat/react';
import { Row, Col, Button, ButtonRow, Form, FormRow, RadioGroup, Option, InputError, Action } from '@op/opds.opux';
import { ProgressTracker } from '@op/opds.opux-progress-tracker';
import { IconPrevious, IconNext } from '@op/opds.opux-icons/navigation';
import './Inquiry.scss';
import { AnalyticsPhaseCategories, analyticsPhases, CardSelectorToolState } from './CardSelectorTool';
import { Age, Card } from '../../common';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { sendAdobeAnalyticsActionData, sendAdobeAnalyticsPhaseData } from '../../util/analytics';

type ButtonType = {
  classicPoints: number;
  debitPoints: number;
  duoPoints: number;
  goldPoints: number;
  platinumPoints: number;
  selected: boolean;
  analyticsAction?: string;
};

type PointsGiven = {
  classicPoints: number;
  debitPoints: number;
  duoPoints: number;
  goldPoints: number;
  platinumPoints: number;
};

type ButtonGroupType = {
  buttonCount: number;
  allowMultipleSelections: boolean;
  pointsGiven: PointsGiven;
  buttons: Array<ButtonType>;
};

interface InquiryProps {
  stateHandler: Function;
  cardPoints: Map<Card, number>;
  setCardPoints: Function;
  setAge: Function;
  setHasInquiryProgressed: Function;
}

const Inquiry = (props: InquiryProps) => {
  const FIRST_STEP = 1;
  const LAST_STEP = 8;

  const sessionStorage = useSessionStorage();

  const [error, setError] = useState<string | undefined>();
  const inputErrorMessage = useMessage('paketinvalintatyokalu:inputerror', '');
  const [inputError, setInputError] = useState<string | null>(null);
  const [wrapRadioGroup, setWrapRadioGroup] = useState<boolean>(() => {
    return window.innerWidth > 850 ? true : false;
  });
  const [step, setStep] = useState<number>(1);
  const [maxStepReached, setMaxStepReached] = useState<number>(1);
  const [disablePrevious, setDisablePrevious] = useState<boolean>(true);
  const [disableNext, setDisableNext] = useState<boolean>(true);
  // prettier-ignore
  const [buttonsPerStep, setButtonsPerStep] = useState<Map<number, ButtonGroupType>>(
    new Map([
      [ 1, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: -2, selected: false, analyticsAction: "18-25 v" }, { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: -1, selected: false, analyticsAction: "26-29 v" }, { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "yli 30 v" }, ], }, ],
      [ 2, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 0, debitPoints: 1, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En ole kiinnostunut" }, { classicPoints: 1, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "6 kk:n turva kiinnostaa" }, { classicPoints: 0, debitPoints: 0, duoPoints: 1, goldPoints: 1, platinumPoints: 1, selected: false, analyticsAction: "12 kk:n turva kiinnostaa" }, ], }, ],
      [ 3, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 0, debitPoints: 1, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En ole kiinnostunut" }, { classicPoints: 1, debitPoints: 0, duoPoints: 1, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "Ehka kiinnostaa" }, { classicPoints: 1, debitPoints: 0, duoPoints: 1, goldPoints: 1, platinumPoints: 1, selected: false, analyticsAction: "Kylla kiinnostaa" }, ], }, ],
      [ 4, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 0, debitPoints: 1, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En lainkaan" }, { classicPoints: 1, debitPoints: 0, duoPoints: 1, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "Jonkin verran" }, { classicPoints: 0, debitPoints: 0, duoPoints: 3, goldPoints: 1, platinumPoints: 1, selected: false, analyticsAction: "Kylla, paljonkin" }, ], }, ],
      [ 5, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 1, debitPoints: 1, duoPoints: 0, goldPoints: 1, platinumPoints: 0, selected: false, analyticsAction: "Ei huoleta" }, { classicPoints: 0, debitPoints: 0, duoPoints: 1, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En ole varma" }, { classicPoints: 0, debitPoints: 0, duoPoints: 1, goldPoints: 0, platinumPoints: 1, selected: false, analyticsAction: "Kylla huolettaa" }, ], }, ],
      [ 6, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 0, debitPoints: 1, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En kay tapahtumissa" }, { classicPoints: 1, debitPoints: 0, duoPoints: 0, goldPoints: 1, platinumPoints: 0, selected: false, analyticsAction: "Kayn tapahtumissa joskus" }, { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 1, platinumPoints: 1, selected: false, analyticsAction: "Kayn tapahtumissa" }, ], }, ],
      [ 7, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 0, debitPoints: 1, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En matkusta" }, { classicPoints: 1, debitPoints: 0, duoPoints: 0, goldPoints: 1, platinumPoints: 0, selected: false, analyticsAction: "Matkustan kerran vuodessa" }, { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 1, platinumPoints: 1, selected: false, analyticsAction: "Matkustan useammin" }, ], }, ],
      [ 8, { buttonCount: 3, allowMultipleSelections: false, pointsGiven: { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0 }, buttons: [ { classicPoints: 1, debitPoints: 0, duoPoints: 0, goldPoints: 0, platinumPoints: 0, selected: false, analyticsAction: "En ole kiinnostunut" }, { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 1, platinumPoints: 0, selected: false, analyticsAction: "Ehka kiinnostaa" }, { classicPoints: 0, debitPoints: 0, duoPoints: 0, goldPoints: 1, platinumPoints: 1, selected: false, analyticsAction: "Kylla kiinnostaa" }, ], }, ],
    ])
  );
  const headingRef = useRef<HTMLHeadingElement>(null);
  const optionsRef = useRef<HTMLFieldSetElement>(null);
  const progressionScreenreader = useMessage('paketinvalintatyokalu:ruudunlukija:edistyminen', { 0: step.toString(), 1: '8' });
  const question1Screenreader = useMessage('paketinvalintatyokalu:kysymys1:otsikko', '');
  const question2Screenreader = useMessage('paketinvalintatyokalu:kysymys2:otsikko', '');
  const question3Screenreader = useMessage('paketinvalintatyokalu:kysymys3:otsikko', '');
  const question4Screenreader = useMessage('paketinvalintatyokalu:kysymys4:otsikko', '');
  const question5Screenreader = useMessage('paketinvalintatyokalu:kysymys5:otsikko', '');
  const question6Screenreader = useMessage('paketinvalintatyokalu:kysymys6:otsikko', '');
  const question7Screenreader = useMessage('paketinvalintatyokalu:kysymys7:otsikko', '');
  const question8Screenreader = useMessage('paketinvalintatyokalu:kysymys8:otsikko', '');
  const question1WithProgressionScreenreader = progressionScreenreader + question1Screenreader;
  const question2WithProgressionScreenreader = progressionScreenreader + question2Screenreader;
  const question3WithProgressionScreenreader = progressionScreenreader + question3Screenreader;
  const question4WithProgressionScreenreader = progressionScreenreader + question4Screenreader;
  const question5WithProgressionScreenreader = progressionScreenreader + question5Screenreader;
  const question6WithProgressionScreenreader = progressionScreenreader + question6Screenreader;
  const question7WithProgressionScreenreader = progressionScreenreader + question7Screenreader;
  const question8WithProgressionScreenreader = progressionScreenreader + question8Screenreader;
  const question1opt1ScreenReader = useMessage('paketinvalintatyokalu:kysymys1:button1:sr', '');
  const question1opt2ScreenReader = useMessage('paketinvalintatyokalu:kysymys1:button2:sr', '');
  // prettier-ignore
  const questionsPerStep = useMemo(() => new Map<number, { ariaLabel: string, ariaLabelWithProgression: string; textId: string }>([
    [1, { ariaLabel: question1Screenreader, ariaLabelWithProgression: question1WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys1:otsikko' }],
    [2, { ariaLabel: question2Screenreader, ariaLabelWithProgression: question2WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys2:otsikko' }],
    [3, { ariaLabel: question3Screenreader, ariaLabelWithProgression: question3WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys3:otsikko' }],
    [4, { ariaLabel: question4Screenreader, ariaLabelWithProgression: question4WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys4:otsikko' }],
    [5, { ariaLabel: question5Screenreader, ariaLabelWithProgression: question5WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys5:otsikko' }],
    [6, { ariaLabel: question6Screenreader, ariaLabelWithProgression: question6WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys6:otsikko' }],
    [7, { ariaLabel: question7Screenreader, ariaLabelWithProgression: question7WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys7:otsikko' }],
    [8, { ariaLabel: question8Screenreader, ariaLabelWithProgression: question8WithProgressionScreenreader, textId: 'paketinvalintatyokalu:kysymys8:otsikko' }],
  ]), [question1Screenreader, question2Screenreader, question3Screenreader, question4Screenreader, question5Screenreader, question6Screenreader, question7Screenreader, question8Screenreader, 
    question1WithProgressionScreenreader, question2WithProgressionScreenreader, question3WithProgressionScreenreader, question4WithProgressionScreenreader, question5WithProgressionScreenreader, question6WithProgressionScreenreader, question7WithProgressionScreenreader, question8WithProgressionScreenreader]);

  const isButtonSelected = (index: number) => {
    return buttonsPerStep.get(step)?.buttons[index].selected;
  };

  const selectButton = (index: number) => {
    const isSelected = isButtonSelected(index);
    const newButtonsPerStep = new Map(buttonsPerStep);
    const buttonGroup = newButtonsPerStep.get(step);

    if (buttonGroup) {
      if (buttonGroup.allowMultipleSelections) {
        buttonGroup.buttons[index].selected = !isSelected;
      } else {
        buttonGroup.buttons.forEach((button, i) => {
          i === index ? (buttonGroup.buttons[i].selected = true) : (buttonGroup.buttons[i].selected = false);
        });
      }
      newButtonsPerStep.set(step, buttonGroup);
      setButtonsPerStep(newButtonsPerStep);
      if (step === FIRST_STEP) props.setHasInquiryProgressed(true);
    }
  };

  const handlePrevious = () => {
    const newCardPoints = new Map<Card, number>(props.cardPoints);
    newCardPoints.set(Card.CLASSIC, newCardPoints.get(Card.CLASSIC)! - buttonsPerStep.get(step - 1)?.pointsGiven.classicPoints!);
    newCardPoints.set(Card.DEBIT, newCardPoints.get(Card.DEBIT)! - buttonsPerStep.get(step - 1)?.pointsGiven.debitPoints!);
    newCardPoints.set(Card.DUO, newCardPoints.get(Card.DUO)! - buttonsPerStep.get(step - 1)?.pointsGiven.duoPoints!);
    newCardPoints.set(Card.GOLD, newCardPoints.get(Card.GOLD)! - buttonsPerStep.get(step - 1)?.pointsGiven.goldPoints!);
    newCardPoints.set(Card.PLATINUM, newCardPoints.get(Card.PLATINUM)! - buttonsPerStep.get(step - 1)?.pointsGiven.platinumPoints!);
    props.setCardPoints(newCardPoints);
    sessionStorage.updateItem('cardPoints', JSON.stringify(Array.from(newCardPoints.entries())));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(step - 1);
  };

  const handleNext = () => {
    if (buttonsPerStep.get(step)?.buttons.some((button) => button.selected)) {
      if (step === FIRST_STEP) handleSetAge();
      calculateCardPoints();
      if (step + 1 > maxStepReached) setMaxStepReached(step + 1);
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      sendAnalyticsActionData();
    } else {
      setInputError(inputErrorMessage);
    }
  };

  const handleFinalStepStateChange = () => {
    if (buttonsPerStep.get(step)?.buttons.some((button) => button.selected)) {
      calculateCardPoints();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      sendAnalyticsActionData();

      props.stateHandler(CardSelectorToolState.RESULT);
      sessionStorage.updateItem('cardSelectorToolState', JSON.stringify(CardSelectorToolState.RESULT));
    } else {
      setInputError(inputErrorMessage);
    }
  };

  const calculateCardPoints = () => {
    let pointsToClassic = 0;
    let pointsToDebit = 0;
    let pointsToDuo = 0;
    let pointsToGold = 0;
    let pointsToPlatinum = 0;
    const newButtonsPerStep = new Map(buttonsPerStep);

    newButtonsPerStep.get(step)?.buttons.forEach((button, i) => {
      if (button.selected) {
        pointsToClassic = pointsToClassic + newButtonsPerStep.get(step)?.buttons[i].classicPoints!;
        pointsToDebit = pointsToDebit + newButtonsPerStep.get(step)?.buttons[i].debitPoints!;
        pointsToDuo = pointsToDuo + newButtonsPerStep.get(step)?.buttons[i].duoPoints!;
        pointsToGold = pointsToGold + newButtonsPerStep.get(step)?.buttons[i].goldPoints!;
        pointsToPlatinum = pointsToPlatinum + newButtonsPerStep.get(step)?.buttons[i].platinumPoints!;
      }
    });

    const buttonGroup: ButtonGroupType = newButtonsPerStep.get(step)!;
    const newPointsGiven = {
      classicPoints: pointsToClassic,
      debitPoints: pointsToDebit,
      duoPoints: pointsToDuo,
      goldPoints: pointsToGold,
      platinumPoints: pointsToPlatinum,
    };
    buttonGroup.pointsGiven = newPointsGiven;
    newButtonsPerStep.set(step, buttonGroup);
    setButtonsPerStep(newButtonsPerStep);

    const newCardPoints = new Map<Card, number>(props.cardPoints);
    newCardPoints.set(Card.CLASSIC, newCardPoints.get(Card.CLASSIC)! + pointsToClassic);
    newCardPoints.set(Card.DEBIT, newCardPoints.get(Card.DEBIT)! + pointsToDebit);
    newCardPoints.set(Card.DUO, newCardPoints.get(Card.DUO)! + pointsToDuo);
    newCardPoints.set(Card.GOLD, newCardPoints.get(Card.GOLD)! + pointsToGold);
    newCardPoints.set(Card.PLATINUM, newCardPoints.get(Card.PLATINUM)! + pointsToPlatinum);
    props.setCardPoints(newCardPoints);
    sessionStorage.updateItem('cardPoints', JSON.stringify(Array.from(newCardPoints.entries())));
  };

  const handleSetAge = () => {
    const index = buttonsPerStep.get(1)?.buttons.findIndex((button) => button.selected);
    switch (index) {
      case 0:
        props.setAge(Age.AGE_18_TO_25);
        sessionStorage.updateItem('age', JSON.stringify(Age.AGE_18_TO_25));
        break;
      case 1:
        props.setAge(Age.AGE_26_TO_29);
        sessionStorage.updateItem('age', JSON.stringify(Age.AGE_26_TO_29));
        break;
      case 2:
        props.setAge(Age.AGE_OVER_30);
        sessionStorage.updateItem('age', JSON.stringify(Age.AGE_OVER_30));
        break;
      default:
        setError('Error defining age');
    }
  };

  const isInvalid = () => {
    if (inputError) return true;
    else return false;
  };

  const sendAnalyticsActionData = () => {
    try {
      let actionName: string;

      buttonsPerStep.get(step)?.buttons.some((button) => {
        if (button.selected && button.analyticsAction) {
          actionName = button.analyticsAction;
          return true;
        } else {
          return false;
        }
      });

      sendAdobeAnalyticsActionData(actionName!, 'click', analyticsPhases.get(step)!);
    } catch {
      // Do nothing
    }
  };

  useEffect(() => {
    const handleResize = () => {
      window.innerWidth > 850 ? setWrapRadioGroup(true) : setWrapRadioGroup(false);
    };

    window.addEventListener('resize', handleResize, false);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    try {
      sendAdobeAnalyticsPhaseData(step === FIRST_STEP ? AnalyticsPhaseCategories.START : AnalyticsPhaseCategories.STEP, analyticsPhases.get(step)!);
    } catch {
      // Do nothing
    }
  }, [step]);

  useEffect(() => {
    setInputError(null);
  }, [step]);

  useEffect(() => {
    setDisablePrevious(step <= 1);
    setDisableNext(step >= maxStepReached);
  }, [step, maxStepReached]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (inputError) optionsRef.current?.focus();
  }, [inputError]);

  useEffect(() => {
    if (error) throw new Error(error);
  }, [error]);

  const renderMiddleContainer = (
    option1MessageId: string,
    option2MessageId: string,
    option3MessageId: string,
    option1AriaLabel?: string,
    option2AriaLabel?: string,
    option3AriaLabel?: string,
  ) => {
    return (
      <div className="inquiry-middle-container">
        <Form stacked>
          <FormRow required className="inquiry-form-row">
            <RadioGroup
              onChange={(ev, value) => {
                selectButton(Number(value));
              }}
              wrap={wrapRadioGroup}
              className="inquiry-radiogroup"
              ref={optionsRef}
              tabIndex={-1}
              aria-label={questionsPerStep.get(step)?.ariaLabel}
            >
              <Option
                value="0"
                checked={isButtonSelected(0)}
                invalid={isInvalid()}
                onClick={() => setInputError(null)}
                className="inquiry-option"
                aria-label={option1AriaLabel}
                aria-describedby="card-helper-input-error"
                data-testid="left"
              >
                <Message id={option1MessageId} />
              </Option>
              <Option
                value="1"
                checked={isButtonSelected(1)}
                invalid={isInvalid()}
                onClick={() => setInputError(null)}
                className="inquiry-option"
                aria-label={option2AriaLabel}
                aria-describedby="card-helper-input-error"
                data-testid="middle"
              >
                <Message id={option2MessageId} />
              </Option>
              <Option
                value="2"
                checked={isButtonSelected(2)}
                invalid={isInvalid()}
                onClick={() => setInputError(null)}
                className="inquiry-option"
                aria-label={option3AriaLabel}
                aria-describedby="card-helper-input-error"
                data-testid="right"
              >
                <Message id={option3MessageId} />
              </Option>
            </RadioGroup>
          </FormRow>
        </Form>
      </div>
    );
  };

  return (
    <div className="inquiry-container">
      <Row className="inquiry-row">
        <Col xs={2} className="inquiry-col">
          <Button
            icon={IconPrevious}
            iconLabel={useMessage('paketinvalintatyokalu:ruudunlukija:edellinen', '')}
            minified
            disabled={disablePrevious}
            onClick={handlePrevious}
            data-testid="top-previous-button"
          />
        </Col>
        <Col xs={8} className="inquiry-col">
          <ProgressTracker steps={8} currentStep={step} className="inquiry-progress-tracker" aria-hidden="true" />
        </Col>
        <Col xs={2} className="inquiry-col">
          <Button
            icon={IconNext}
            iconLabel={useMessage('paketinvalintatyokalu:ruudunlukija:seuraava', '')}
            minified
            disabled={disableNext}
            onClick={step === LAST_STEP ? handleFinalStepStateChange : handleNext}
            data-testid="next-button"
          />
        </Col>
      </Row>

      <h2
        className="inquiry-h2"
        ref={headingRef}
        aria-label={questionsPerStep.get(step)?.ariaLabelWithProgression}
        tabIndex={-1}
        data-testid="question-heading"
        aria-hidden="true"
      >
        <Message id={questionsPerStep.get(step)?.textId || ''} />
      </h2>

      {step === FIRST_STEP && (
        <>
          <p className="inquiry-text-body inquiry-p-preceding-p">
            <Message id="paketinvalintatyokalu:kysymys1:tarkennus:part1" />
          </p>
          <p className="inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys1:tarkennus:part2" />
            <Action href="/henkiloasiakkaat/paivittaiset/kortit/op-visa-basic" inline>
              <Message id="paketinvalintatyokalu:kysymys1:tarkennus:part3" />
            </Action>
            <Message id="paketinvalintatyokalu:kysymys1:tarkennus:part4" />
            <Action href="/henkiloasiakkaat/paivittaiset/kortit/op-visa-debit" inline>
              <Message id="paketinvalintatyokalu:kysymys1:tarkennus:part5" />
            </Action>
            <Message id="paketinvalintatyokalu:kysymys1:tarkennus:part6" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys1:button1',
            'paketinvalintatyokalu:kysymys1:button2',
            'paketinvalintatyokalu:kysymys1:button3',
            question1opt1ScreenReader,
            question1opt2ScreenReader,
          )}
        </>
      )}

      {step === 2 && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys2:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys2:button1',
            'paketinvalintatyokalu:kysymys2:button2',
            'paketinvalintatyokalu:kysymys2:button3',
          )}
        </>
      )}

      {step === 3 && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys3:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys3:button1',
            'paketinvalintatyokalu:kysymys3:button2',
            'paketinvalintatyokalu:kysymys3:button3',
          )}
        </>
      )}

      {step === 4 && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys4:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys4:button1',
            'paketinvalintatyokalu:kysymys4:button2',
            'paketinvalintatyokalu:kysymys4:button3',
          )}
        </>
      )}

      {step === 5 && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys5:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys5:button1',
            'paketinvalintatyokalu:kysymys5:button2',
            'paketinvalintatyokalu:kysymys5:button3',
          )}
        </>
      )}

      {step === 6 && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys6:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys6:button1',
            'paketinvalintatyokalu:kysymys6:button2',
            'paketinvalintatyokalu:kysymys6:button3',
          )}
        </>
      )}

      {step === 7 && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys7:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys7:button1',
            'paketinvalintatyokalu:kysymys7:button2',
            'paketinvalintatyokalu:kysymys7:button3',
          )}
        </>
      )}

      {step === LAST_STEP && (
        <>
          <p className="inquiry-text-body inquiry-p">
            <Message id="paketinvalintatyokalu:kysymys8:tarkennus" />
          </p>
          {renderMiddleContainer(
            'paketinvalintatyokalu:kysymys8:button1',
            'paketinvalintatyokalu:kysymys8:button2',
            'paketinvalintatyokalu:kysymys8:button3',
          )}
        </>
      )}

      <div className="inquiry-input-error-container" aria-atomic="true" aria-live="assertive">
        <InputError
          error={inputError}
          success={false}
          id="card-helper-input-error"
          onClick={() => setInputError(null)}
          className="inquiry-input-error"
          data-testid="input-error"
        />
      </div>

      <ButtonRow style={{ textAlign: 'center', marginLeft: '4%', marginRight: '4%', paddingTop: '0.75rem' }}>
        {step === 8 ? (
          <>
            <Button primary onClick={handleFinalStepStateChange} data-testid="show-result">
              <Message id="paketinvalintatyokalu:button:nayta" />
            </Button>
            <Button onClick={handlePrevious} data-testid="bottom-previous-button">
              <Message id="paketinvalintatyokalu:button:edellinen" />
            </Button>
          </>
        ) : (
          <>
            <Button primary onClick={handleNext} data-testid="continue-button">
              <Message id="paketinvalintatyokalu:button:jatka" />
            </Button>
            <Button onClick={handlePrevious} style={step === FIRST_STEP ? { visibility: 'hidden' } : {}} data-testid="bottom-previous-button">
              <Message id="paketinvalintatyokalu:button:edellinen" />
            </Button>
          </>
        )}
      </ButtonRow>
    </div>
  );
};

export default Inquiry;
